import React, { useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { tr } from 'mmfintech-commons'
import { useAuthRequest, useLogoutRequest } from 'mmfintech-checkout-commons'

import { Button } from 'mmfintech-portal-commons'
import { TailSpin } from 'react-loader-spinner'
import { MerchantPane } from '../../components'
import { PaymentContainer } from '../CheckoutPayment/CheckoutPaymentCore.styled'
import { WalletCheckoutOtp } from './WalletCheckoutOtp'
import { WalletCheckoutLogin } from './WalletCheckoutLogin'
import { WalletCheckoutAccount } from './WalletCheckoutAccount'
import { WalletCheckoutPreview } from './WalletCheckoutPreview'

interface WalletCheckoutProps {
  pis: boolean
  sessionId: string
  handlePay: (accountId: number, method?: string) => void
  onCancel?: () => void
  paymentMethod: string
  hasStorageAccess: boolean
  setStorageAccess: (enable: boolean) => void
}

enum CheckoutSteps {
  LOADING = 'LOADING',
  OPEN_POPUP = 'OPEN_POPUP',
  REQUEST_ACCESS = 'REQUEST_ACCESS',
  LOGIN = 'LOGIN',
  OTP = 'OTP',
  SELECT_ACCOUNT = 'SELECT_ACCOUNT',
  PREVIEW = 'PREVIEW'
}

export const WalletCheckout: React.FC<WalletCheckoutProps> = props => {
  const { pis, sessionId, handlePay, onCancel, paymentMethod, hasStorageAccess, setStorageAccess } = props

  const { logo, session } = useSelector(
    ({ checkout: { logo, session } }) => ({
      logo,
      session
    }),
    shallowEqual
  )

  const { authRequest } = useAuthRequest()
  const { logoutRequest } = useLogoutRequest()

  const [step, setStep] = useState<CheckoutSteps>(CheckoutSteps.LOADING)
  const [selectedAccount, setSelectedAccount] = useState(null)

  const handleCancel = () => {
    logoutRequest()
    typeof onCancel === 'function' && onCancel()
  }

  const handleLoginResponse = (response: any) => {
    setStep(response?.data?.challenge ? CheckoutSteps.OTP : CheckoutSteps.SELECT_ACCOUNT)
  }

  const requestAccess = () => {
    const noAccess = () => {
      console.error('Cannot obtain storage access')
      typeof onCancel === 'function' && onCancel()
    }

    const continueWithPayment = () => {
      setStorageAccess(true)
      authRequest(
        () => setStep(CheckoutSteps.SELECT_ACCOUNT),
        () => setStep(CheckoutSteps.LOGIN)
      )
    }

    if (typeof document.requestStorageAccess === 'function') {
      document
        .requestStorageAccess()
        .then(
          () => continueWithPayment(),
          () => noAccess()
        )
        .catch(() => {
          noAccess()
        })
    } else {
      continueWithPayment()
    }
  }

  const openPopup = () => {
    setStep(CheckoutSteps.LOADING)
    const handler = window.open(
      `${window.origin}/auth/sessionId/${sessionId}`,
      '_blank',
      'popup=1,location=0,toolbar=0,menubar=0,status=0' //,width=340,height=350
    )
    if (handler) {
      const interval = setInterval(() => {
        if (handler.closed) {
          clearInterval(interval)
          //@ts-ignore
          if (handler.userLoggedIn) {
            setStep(CheckoutSteps.REQUEST_ACCESS)
          } else {
            typeof onCancel === 'function' && onCancel()
          }
        }
      }, 500)
    } else {
      typeof onCancel === 'function' && onCancel()
    }
  }

  useEffect(() => {
    if (hasStorageAccess) {
      setStep(!session.isSubsequent ? CheckoutSteps.LOGIN : CheckoutSteps.SELECT_ACCOUNT)
    } else {
      setStep(CheckoutSteps.OPEN_POPUP)
    }

    return () => {
      setStep(CheckoutSteps.LOADING)
    }
  }, [])

  return (
    <>
      {step !== CheckoutSteps.LOADING && <MerchantPane session={session} logo={logo} />}

      {step == CheckoutSteps.LOADING && (
        <div className='mb-4 mt-4'>
          <TailSpin width='8rem' height='8rem' color='white' />
        </div>
      )}

      {step == CheckoutSteps.OPEN_POPUP && (
        <PaymentContainer data-test='wallet-checkout-request-access'>
          <div className='inner-subtitle'>{tr('CHECKOUT.WALLET_PAYMENT.OPEN_POPUP_TITLE', 'Sign in')}</div>
          <p>
            {tr(
              'CHECKOUT.WALLET_PAYMENT.OPEN_POPUP',
              'In order to continue with Jeton, please click the button below. A new popup window will appear.'
            )}
          </p>

          <div className='mt-4'>
            <Button
              type='button'
              color='alternative'
              text={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
              onClick={openPopup}
            />
            {typeof onCancel === 'function' && (
              <Button
                type='button'
                color='secondary'
                className='mt-1'
                text={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
                onClick={onCancel}
                data-test='button-back'
              />
            )}
          </div>
        </PaymentContainer>
      )}

      {step == CheckoutSteps.REQUEST_ACCESS && (
        <PaymentContainer data-test='wallet-checkout-request-access'>
          <div className='inner-subtitle'>{tr('CHECKOUT.WALLET_PAYMENT.ACCESS_REQUEST_TITLE', 'Cookie consent')}</div>
          <p>
            {tr(
              'CHECKOUT.WALLET_PAYMENT.ACCESS_REQUEST',
              'Our website use cookies to provide safe user experience. Please click the button below to accept.'
            )}
          </p>

          <div className='mt-4'>
            <Button
              type='button'
              color='alternative'
              text={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
              onClick={requestAccess}
            />
            {typeof onCancel === 'function' && (
              <Button
                type='button'
                color='secondary'
                className='mt-1'
                text={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
                onClick={onCancel}
                data-test='button-back'
              />
            )}
          </div>
        </PaymentContainer>
      )}

      {step === CheckoutSteps.LOGIN && (
        <WalletCheckoutLogin
          sessionId={sessionId}
          session={session}
          onSuccess={handleLoginResponse}
          onCancel={onCancel}
        />
      )}

      {step === CheckoutSteps.OTP && (
        <WalletCheckoutOtp onSuccess={() => setStep(CheckoutSteps.SELECT_ACCOUNT)} onCancel={onCancel} wallet={false} />
      )}

      {step === CheckoutSteps.SELECT_ACCOUNT && (
        <WalletCheckoutAccount
          pis={pis}
          onSuccess={() => setStep(CheckoutSteps.PREVIEW)}
          onCancel={handleCancel}
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
          paymentMethod={paymentMethod}
        />
      )}

      {step === CheckoutSteps.PREVIEW && (
        <WalletCheckoutPreview
          pis={pis}
          onCancel={onCancel}
          handlePay={() => handlePay(selectedAccount.id, paymentMethod)}
          selectedAccount={selectedAccount}
        />
      )}
    </>
  )
}
